import { outstandingOf } from "@/features/leads/components/view-lead/components/Ongoing/installments/installmentMath";

/**
 * Pure helpers for the "Collect payment" dialog (CollectPaymentDialog): what a
 * deal can be billed, what the counsellor typed, and which instalment a link
 * settles.
 */

export interface PayableInstallment {
  id: number;
  seq: number;
  dueDate: string;
  outstanding: number;
}

export interface PayableDeal {
  id: number;
  name: string;
  stage: string;
  /** Net value after discounts — what the program costs this student. */
  value: number;
  paid: number;
  pending: number;
  /** False = no program / variant attached yet, so nothing to bill. */
  hasProgram: boolean;
  /** The plan's unpaid instalments, earliest first (empty without a plan). */
  installments: PayableInstallment[];
}

interface RawInstallment {
  id: number;
  seq: number;
  dueDate: string;
  status?: string;
  amount: number;
  paidAmount: number;
  waivedAmount?: number;
}

export interface RawDeal {
  id: number | string;
  name?: string | null;
  stage?: { name?: string | null } | null;
  totalDealValue?: number | null;
  netAmount?: number | null;
  totalPaid?: number | null;
  pendingAmount?: number | null;
  isFroze?: boolean | null;
  installmentPlan?: { installments?: RawInstallment[] | null } | null;
}

const num = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function toPayableDeal(raw: RawDeal): PayableDeal {
  const plan = raw.installmentPlan?.installments;
  const installments = (Array.isArray(plan) ? plan : [])
    .filter((i) => i.status !== "PAID" && i.status !== "WAIVED")
    .sort((a, b) => a.seq - b.seq)
    .map((i) => ({ id: i.id, seq: i.seq, dueDate: i.dueDate, outstanding: outstandingOf(i) }))
    .filter((i) => i.outstanding > 0);
  const total = num(raw.totalDealValue);
  const net = num(raw.netAmount);
  return {
    id: Number(raw.id),
    name: raw.name || `Deal ${raw.id}`,
    stage: raw.stage?.name ?? "",
    value: net || total,
    paid: num(raw.totalPaid),
    pending: Math.max(0, num(raw.pendingAmount)),
    // The deal card's own test (DealPaymentCard.hasNoVariant).
    hasProgram: !(total === 0 && net === 0),
    installments,
  };
}

/**
 * "25,000" / "₹ 6,999.50" → 25000 / 6999.5. A comma is digit grouping, never
 * a decimal point (the Android keyboard bug: parseFloat("25,000") = 25).
 * Returns null for anything that is not a positive amount with ≤ 2 decimals.
 */
export function parseRupees(input: string): number | null {
  const cleaned = String(input ?? "").replace(/[₹,\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return n > 0 ? Math.round(n * 100) / 100 : null;
}

/** 6999 → "6,999"; 6999.5 → "6,999.5". */
export function formatRupees(n: number): string {
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

/**
 * Which instalment a link of `amount` bills. A deal with a plan: an amount up
 * to the next instalment's balance settles THAT instalment (as Record payment
 * does); a bigger amount is a plain payment on the deal. No plan → none.
 */
export function installmentForAmount(
  amount: number,
  installments: PayableInstallment[],
): PayableInstallment | null {
  const next = installments[0];
  if (!next) return null;
  return amount <= next.outstanding + 0.005 ? next : null;
}

/** The amount the dialog opens with: the next instalment, else the whole balance. */
export function defaultAmountFor(deal: PayableDeal): number {
  const next = deal.installments[0];
  return next ? Math.min(next.outstanding, deal.pending) : deal.pending;
}
